import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'
import { fieldControlStatus, useFieldStatus } from './field'

/**
 * Input — single-line text control, from the Figma DS2026 "Input field" spec
 * (First Bite Design System, node 1536:18108), wired to First Bite tokens:
 *
 *   status  default | error | success   (border + 4px focus ring color)
 *   size    sm | md | lg                  (32px/12px text · 40px · 44px, both 14px)
 *
 * White surface, gray-300 border, 8px radius; placeholder gray-400; typed text
 * gray-800. Focus swaps the border to the status color and adds the translucent
 * 4px ring (`--fbui-ring-*`). Disabled uses Figma's explicit gray (gray-50
 * surface, gray-400 text), not opacity.
 *
 * Inside a `<Field>` the status is inherited from context; pass `status` here to
 * override it or to use the input standalone. Pair with `<FieldLabel>` /
 * `<FieldHint>` for the labelled + helper-text layouts.
 */
export const inputVariants = cva(
  cn(
    'w-full rounded-lg border bg-fbui-base-white text-fbui-gray-800 tracking-[-0.2px]',
    'placeholder:text-fbui-gray-400',
    'outline-none transition focus:ring-4',
    'disabled:cursor-not-allowed disabled:bg-fbui-gray-50 disabled:text-fbui-text-disabled disabled:border-fbui-gray-200'
  ),
  {
    variants: {
      status: fieldControlStatus,
      size: {
        sm: 'h-8 px-3 py-1.5 text-xs',
        md: 'h-10 px-3.5 py-2 text-sm',
        lg: 'h-11 px-3.5 text-sm',
      },
    },
    defaultVariants: { status: 'default', size: 'md' },
  }
)

export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    Omit<VariantProps<typeof inputVariants>, 'status'> {
  /** Border + ring color. Inherited from a parent `<Field>` when omitted. */
  status?: VariantProps<typeof inputVariants>['status']
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, status, size, type = 'text', ...props }, ref) => {
    const resolved = useFieldStatus(status ?? undefined)
    return (
      <input
        ref={ref}
        type={type}
        aria-invalid={resolved === 'error' || undefined}
        className={cn(inputVariants({ status: resolved, size, className }))}
        {...props}
      />
    )
  }
)
Input.displayName = 'Input'
